import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const inputCls = "h-12 rounded-xl border-white/10 bg-white/5";

const REGIONS = ["Adamaoua", "Centre", "Est", "Extrême-Nord", "Littoral", "Nord", "Nord-Ouest", "Ouest", "Sud", "Sud-Ouest"];
const REGIONS_EN = { Adamaoua: "Adamawa", Est: "East", "Extrême-Nord": "Far North", Nord: "North", "Nord-Ouest": "North-West", Ouest: "West", Sud: "South", "Sud-Ouest": "South-West" };

const Kyc = ({ data, reload }) => {
  const { t, tErr, lang } = useI18n();
  const [f, setF] = useState({ full_name: "", doc_type: "cni", doc_number: "", region: "" });
  const [busy, setBusy] = useState(false);
  const status = data.kyc_status || "none";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    toast.dismiss();
    try {
      await api.post("/me/kyc", f);
      toast.success(t("resp.kycSent"));
      reload();
    } catch (err) {
      toast.error(tErr(errCode(err)));
    } finally {
      setBusy(false);
    }
    return null;
  };
  return (
    <section className="surface space-y-4 p-5" data-testid="kyc-section">
      <h2 className="font-display text-lg font-bold">{t("profile.kyc")}</h2>
      <p className="text-sm text-slate-400" data-testid="kyc-status">{t(`resp.kyc_${status}`)}{data.kyc_reason ? ` — ${data.kyc_reason}` : ""}</p>
      {["none", "rejected"].includes(status) && (
        <form onSubmit={submit} className="space-y-3">
          <Input placeholder={t("resp.fullName")} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className={inputCls} required data-testid="kyc-name-input" />
          <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className="h-12 w-full rounded-xl border border-white/10 bg-[#121620] px-3" data-testid="kyc-type-select">
            <option value="cni">{t("resp.cni")}</option><option value="passport">{t("resp.passport")}</option><option value="permit">{t("resp.permit")}</option>
          </select>
          <Input placeholder={t("resp.docNumber")} value={f.doc_number} onChange={(e) => setF({ ...f, doc_number: e.target.value })} className={inputCls} required data-testid="kyc-number-input" />
          <select value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} required className="h-12 w-full rounded-xl border border-white/10 bg-[#121620] px-3" data-testid="kyc-region-select">
            <option value="" disabled>{t("resp.region")}</option>
            {REGIONS.map((r) => <option key={r} value={r}>{lang === "en" ? REGIONS_EN[r] || r : r}</option>)}
          </select>
          <p className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="kyc-verify-note">{t("resp.kycNote")}</p>
          <button disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-semibold text-[#0B0D12] disabled:opacity-60" data-testid="kyc-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? t("resp.sending") : t("resp.kycSubmit")}
          </button>
        </form>
      )}
      {status === "pending" && <p className="text-xs text-slate-400" data-testid="kyc-pending-note">{t("resp.kycPendingNote")}</p>}
    </section>
  );
};

const Limits = ({ data, reload }) => {
  const { t, tErr, lang } = useI18n();
  const lim = data.limits || {};
  const [d, setD] = useState("");
  const [w, setW] = useState("");
  const save = async () => {
    const body = {};
    if (d) body.deposit_daily = parseInt(d, 10);
    if (w) body.deposit_weekly = parseInt(w, 10);
    try {
      await api.put("/me/limits", body);
      toast.success(t("resp.saved"));
      reload();
    } catch (err) {
      toast.error(tErr(errCode(err)));
    }
  };
  const exclude = async (days) => {
    if (!window.confirm(t("resp.excludeConfirm", { days }))) return;
    try {
      await api.post("/me/self-exclude", { days });
      toast.success(t("resp.saved"));
      reload();
    } catch (err) {
      toast.error(tErr(errCode(err)));
    }
  };
  return (
    <section className="surface space-y-4 p-5" data-testid="limits-section">
      <h2 className="font-display text-lg font-bold">{t("legal.responsible")}</h2>
      <p className="text-sm text-slate-400">{t("resp.current")} : {t("resp.daily")} {lim.deposit_daily ? formatXAF(lim.deposit_daily, lang) : "—"} · {t("resp.weekly")} {lim.deposit_weekly ? formatXAF(lim.deposit_weekly, lang) : "—"}</p>
      <div className="grid grid-cols-2 gap-3">
        <Input type="number" placeholder={t("resp.daily")} value={d} onChange={(e) => setD(e.target.value)} className={inputCls} data-testid="limit-daily-input" />
        <Input type="number" placeholder={t("resp.weekly")} value={w} onChange={(e) => setW(e.target.value)} className={inputCls} data-testid="limit-weekly-input" />
      </div>
      <p className="text-xs text-slate-500">{t("resp.limitHint")}</p>
      <button onClick={save} className="h-12 w-full rounded-full border border-amber-500/40 font-semibold text-amber-300" data-testid="limits-save-btn">{t("resp.save")}</button>
      <div className="border-t border-white/5 pt-4">
        <p className="mb-3 text-sm text-slate-300" data-testid="exclusion-status">{data.self_excluded_until ? t("resp.excludedUntil", { date: new Date(data.self_excluded_until).toLocaleString(lang === "en" ? "en-GB" : "fr-FR") }) : t("resp.exclude")}</p>
        <div className="flex flex-wrap gap-2">{[1, 7, 30, 90].map((n) => <button key={n} onClick={() => exclude(n)} className="h-10 rounded-full border border-red-500/40 px-4 text-sm text-red-300" data-testid={`exclude-${n}`}>{t("resp.days", { n })}</button>)}</div>
      </div>
    </section>
  );
};

export default function ResponsiblePage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["responsible"], queryFn: async () => (await api.get("/me/responsible")).data });
  const reload = () => {
    refetch();
    qc.invalidateQueries({ queryKey: ["me"] });
  };
  if (!data) return null;
  return (
    <div className="mx-auto max-w-2xl space-y-5 rise" data-testid="responsible-page">
      <h1 className="font-display text-3xl font-extrabold">{t("resp.title")}</h1>
      <Kyc data={data} reload={reload} />
      <Limits data={data} reload={reload} />
    </div>
  );
}
